import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  createMessageThread,
  fetchMessageThreads,
  fetchThreadMessages,
  markThreadRead,
  sendThreadMessage,
} from '../../api/guestApi';
import { useToast } from '../../components/ToastContext';

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const GuestMessages = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  const hostId = searchParams.get('hostId');
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [initHandled, setInitHandled] = useState(false);
  const listRef = useRef(null);

  const loadMessages = useCallback(async (threadId, silent = false) => {
    if (!threadId) return;
    if (!silent) setLoadingMessages(true);
    if (!silent) setError('');
    try {
      const res = await fetchThreadMessages(threadId, { page: 1, limit: 100 });
      setMessages(res.messages || []);
      setSelectedThread((prev) => (prev?.id === threadId ? { ...prev, ...res.thread } : res.thread));
    } catch (err) {
      if (!silent) setError(err.message || 'Unable to load messages');
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  const loadThreads = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingThreads(true);
      if (!silent) setError('');
      try {
        const res = await fetchMessageThreads({ page: 1, limit: 50 });
        const items = res.data || [];
        setThreads(items);
        let shouldLoadFirst = false;
        let nextSelected = null;
        setSelectedThread((prev) => {
          if (!items.length) {
            nextSelected = null;
            return null;
          }
          if (!prev) {
            shouldLoadFirst = true;
            nextSelected = items[0];
            return items[0];
          }
          const updated = items.find((item) => Number(item.id) === Number(prev.id));
          nextSelected = updated || prev;
          return nextSelected;
        });
        if (!items.length) {
          setMessages([]);
        }
        if (shouldLoadFirst && nextSelected) {
          await loadMessages(nextSelected.id, true);
        }
      } catch (err) {
        if (!silent) setError(err.message || 'Unable to load threads');
      } finally {
        if (!silent) setLoadingThreads(false);
      }
    },
    [loadMessages]
  );

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    await loadMessages(thread.id);
    try {
      await markThreadRead(thread.id);
      await loadThreads(true);
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (initHandled) return;
    if (!bookingId && !hostId) {
      setInitHandled(true);
      return;
    }
    const init = async () => {
      try {
        const payload = bookingId ? { booking_id: bookingId } : { host_id: hostId };
        const thread = await createMessageThread(payload);
        if (thread?.id) {
          setSelectedThread(thread);
          await loadMessages(thread.id);
          await markThreadRead(thread.id);
        }
        await loadThreads(true);
      } catch (err) {
        toast.error(err.message || 'Unable to open conversation');
      } finally {
        setInitHandled(true);
      }
    };
    init();
  }, [bookingId, hostId, initHandled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, selectedThread?.id]);

  const handleSend = async (event) => {
    event.preventDefault();
    if (!selectedThread?.id) return;
    const text = messageText.trim();
    if (!text) return;
    setSending(true);
    try {
      const msg = await sendThreadMessage(selectedThread.id, { text });
      setMessages((prev) => [...prev, msg]);
      setMessageText('');
      await markThreadRead(selectedThread.id);
      await loadThreads(true);
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const activeThread = useMemo(
    () => threads.find((item) => Number(item.id) === Number(selectedThread?.id)) || selectedThread,
    [threads, selectedThread]
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">Threads</p>
            <p className="text-xs text-slate-500">Guest and host conversations.</p>
          </div>
          {loadingThreads && <div className="px-4 py-3 text-sm text-slate-500">Loading...</div>}
          {!loadingThreads && threads.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500">No conversations yet.</div>
          )}
          <div className="divide-y divide-slate-100">
            {threads.map((thread) => {
              const isActive = Number(thread.id) === Number(activeThread?.id);
              return (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => handleSelectThread(thread)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${
                    isActive ? 'bg-emerald-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{thread.property_title || 'Booking'}</p>
                      <p className="text-xs text-slate-500">
                        {thread.booking_code ? `Booking ${thread.booking_code}` : 'Direct thread'}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">{thread.host_name || 'Host'}</p>
                    </div>
                    {thread.unread_count > 0 && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        {thread.unread_count}
                      </span>
                    )}
                  </div>
                  {thread.last_message_preview && (
                    <p className="mt-2 text-xs text-slate-500 line-clamp-2">{thread.last_message_preview}</p>
                  )}
                  {thread.last_message_at && (
                    <p className="mt-1 text-[11px] text-slate-400">{formatDateTime(thread.last_message_at)}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm flex flex-col">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">
              {activeThread?.property_title || 'Messages'}
            </p>
            <p className="text-xs text-slate-500">
              {activeThread?.booking_code ? `Booking ${activeThread.booking_code}` : 'Select a thread to chat'}
            </p>
          </div>

          {loadingMessages && <div className="px-4 py-4 text-sm text-slate-500">Loading messages...</div>}

          {!loadingMessages && !activeThread && (
            <div className="flex-1 px-4 py-6 text-sm text-slate-500">Select a thread to view messages.</div>
          )}

          {!loadingMessages && activeThread && (
            <>
              <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-sm text-slate-500">No messages yet. Start the conversation.</div>
                )}
                {messages.map((msg) => {
                  const isGuest = msg.sender_type === 'guest';
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isGuest ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${
                          isGuest ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.body}</p>
                        <p className={`mt-1 text-[11px] ${isGuest ? 'text-emerald-100' : 'text-slate-400'}`}>
                          {formatDateTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleSend} className="border-t border-slate-100 px-4 py-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type your message"
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={sending || !messageText.trim()}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestMessages;
